"use client";

import { Badge, Button, cn, Loader } from "@devhop/ui";
import { format } from "date-fns";
import {
  ArrowLeftRight,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Clock,
  LogIn,
  UserRound,
} from "lucide-react";
import { useEffect, useMemo } from "react";
import type {
  ShopShiftReportByDayRow,
  ShopShiftReportByStaffRow,
  ShopShiftSwapRequestRow,
} from "@/modules/merchant/presentation/api/client";
import {
  completionRate,
  type ShiftReportTotals,
} from "@/modules/merchant/presentation/lib/shift-report-stats";
import { formatDateLocal } from "@/shared/lib/date-time";

function ReportSummaryCards({ totals }: { totals: ShiftReportTotals }) {
  const rate = completionRate(totals.fullyClocked, totals.assignments);

  const cards = [
    {
      label: "ກະທັງໝົດ",
      value: totals.assignments,
      icon: CalendarDays,
      className: "border-border bg-muted/25",
    },
    {
      label: "ມີເວລາເຂົ້າ",
      value: totals.clockIn,
      icon: LogIn,
      className:
        "border-sky-500/30 bg-sky-500/10 text-sky-950 dark:text-sky-100",
    },
    {
      label: "ຄົບ (ມີອອກ)",
      value: totals.fullyClocked,
      icon: CheckCircle2,
      className:
        "border-emerald-500/30 bg-emerald-500/10 text-emerald-950 dark:text-emerald-100",
    },
    {
      label: "ອັດຕາຄົບ",
      value: `${rate}%`,
      icon: BarChart3,
      className: "border-border bg-card",
      isPercent: true,
    },
  ] as const;

  return (
    <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
      {cards.map((card) => (
        <div
          key={card.label}
          className={cn(
            "rounded-xl border px-3 py-3 sm:px-4 sm:py-3.5",
            card.className,
          )}
        >
          <div className="flex items-center gap-1.5">
            <card.icon className="size-3.5 shrink-0 opacity-70" aria-hidden />
            <p className="font-medium text-[10px] uppercase tracking-wide opacity-80">
              {card.label}
            </p>
          </div>
          <p className="mt-1 font-bold font-heading text-2xl tabular-nums tracking-tight">
            {card.value}
          </p>
        </div>
      ))}
    </div>
  );
}

function MetricBar({
  label,
  value,
  max,
  tone,
}: {
  label: string;
  value: number;
  max: number;
  tone: "sky" | "emerald" | "muted";
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const barClass =
    tone === "sky"
      ? "bg-sky-500"
      : tone === "emerald"
        ? "bg-emerald-500"
        : "bg-muted-foreground/40";

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-medium tabular-nums">{value}</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className={cn("h-full rounded-full transition-all", barClass)}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function DayReportRow({
  row,
  maxAssignments,
  highlighted,
}: {
  row: ShopShiftReportByDayRow;
  maxAssignments: number;
  highlighted: boolean;
}) {
  const todayKey = format(new Date(), "yyyy-MM-dd");
  const today = row.workDate === todayKey;

  return (
    <li
      id={`report-day-${row.workDate}`}
      className={cn(
        "scroll-mt-4 px-4 py-4 sm:px-5",
        highlighted && "bg-primary/5",
        today && "ring-1 ring-inset ring-emerald-500/20",
      )}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-foreground text-sm tabular-nums">
            {formatDateLocal(row.workDate)}
          </h3>
          {today ? (
            <Badge variant="secondary" className="text-[10px]">
              ມື້ນີ້
            </Badge>
          ) : null}
        </div>
        <span className="font-bold text-foreground text-lg tabular-nums">
          {row.assignmentCount}
          <span className="ml-1 font-normal text-muted-foreground text-xs">
            ກະ
          </span>
        </span>
      </div>

      {row.assignmentCount === 0 ? (
        <p className="text-muted-foreground text-sm">ບໍ່ມີກະ</p>
      ) : (
        <div className="space-y-2.5">
          <MetricBar
            label="ມີເວລາເຂົ້າ"
            value={row.withClockInCount}
            max={row.assignmentCount}
            tone="sky"
          />
          <MetricBar
            label="ຄົບ (ມີອອກ)"
            value={row.fullyClockedCount}
            max={row.assignmentCount}
            tone="emerald"
          />
          <MetricBar
            label="ເທທຽບກັບວັນສູງສຸດ"
            value={row.assignmentCount}
            max={maxAssignments}
            tone="muted"
          />
        </div>
      )}
    </li>
  );
}

function StaffReportRow({ row }: { row: ShopShiftReportByStaffRow }) {
  const displayName =
    row.staffUserName?.trim() || row.staffUserEmail.split("@")[0];
  const rate = completionRate(row.fullyClockedCount, row.assignmentCount);

  return (
    <li className="px-4 py-3.5 sm:px-5">
      <div className="flex gap-3 sm:items-center">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-sky-500/10 text-sky-700 dark:text-sky-300">
          <UserRound className="size-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-semibold text-foreground text-base">
              {displayName}
            </p>
            <Badge variant="outline" className="text-[10px] tabular-nums">
              {rate}% ຄົບ
            </Badge>
          </div>
          {row.staffUserName?.trim() ? (
            <p className="truncate text-muted-foreground text-xs">
              {row.staffUserEmail}
            </p>
          ) : null}
          <div className="mt-2 grid grid-cols-3 gap-2">
            <StatPill label="ກະ" value={row.assignmentCount} />
            <StatPill label="ເຂົ້າ" value={row.withClockInCount} />
            <StatPill label="ອອກ" value={row.fullyClockedCount} />
          </div>
        </div>
      </div>
    </li>
  );
}

function StatPill({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 px-2 py-1.5 text-center">
      <p className="font-bold text-foreground text-sm tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

function SwapPendingCard({
  row,
  busy,
  onApprove,
  onReject,
}: {
  row: ShopShiftSwapRequestRow;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const who = row.requesterUserName?.trim() || row.requesterUserEmail;

  return (
    <li className="rounded-xl border border-amber-500/25 bg-amber-500/[0.04] p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="secondary" className="text-[10px]">
              ລໍຖ້າອະນຸມັດ
            </Badge>
            <span className="font-medium text-foreground text-sm tabular-nums">
              {formatDateLocal(row.workDate)}
            </span>
          </div>
          <p className="mt-2 font-semibold text-foreground text-sm">{who}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-muted-foreground text-sm">
            <span className="truncate">{row.sourceStaffUserEmail}</span>
            <ArrowLeftRight className="size-4 shrink-0" aria-hidden />
            <span className="truncate">{row.targetStaffUserEmail}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onReject}
          >
            ປະຕິເສດ
          </Button>
          <Button type="button" size="sm" disabled={busy} onClick={onApprove}>
            ອະນຸມັດ
          </Button>
        </div>
      </div>
    </li>
  );
}

export type ShopShiftReportPanelsProps = {
  loading: boolean;
  totals: ShiftReportTotals;
  byDay: ShopShiftReportByDayRow[];
  byStaff: ShopShiftReportByStaffRow[];
  weekDays: Date[];
  swapPending: ShopShiftSwapRequestRow[];
  swapBusy: boolean;
  onApproveSwap: (id: string) => void;
  onRejectSwap: (id: string) => void;
  highlightDayKey: string | null;
  scrollToDayKey: string | null;
  onScrolledToDay: () => void;
};

export function ShopShiftReportPanels({
  loading,
  totals,
  byDay,
  byStaff,
  weekDays,
  swapPending,
  swapBusy,
  onApproveSwap,
  onRejectSwap,
  highlightDayKey,
  scrollToDayKey,
  onScrolledToDay,
}: ShopShiftReportPanelsProps) {
  useEffect(() => {
    if (!scrollToDayKey) return;
    const el = document.getElementById(`report-day-${scrollToDayKey}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
    onScrolledToDay();
  }, [scrollToDayKey, onScrolledToDay]);

  const byDayMap = useMemo(() => {
    const m = new Map<string, ShopShiftReportByDayRow>();
    for (const row of byDay) m.set(row.workDate, row);
    return m;
  }, [byDay]);

  const weekRows = weekDays.map((day) => {
    const key = format(day, "yyyy-MM-dd");
    return (
      byDayMap.get(key) ?? {
        workDate: key,
        assignmentCount: 0,
        withClockInCount: 0,
        fullyClockedCount: 0,
      }
    );
  });

  const maxAssignments = Math.max(
    1,
    ...weekRows.map((r) => r.assignmentCount),
  );

  if (loading) {
    return (
      <div className="flex justify-center rounded-xl border border-border bg-card py-20 shadow-sm">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <ReportSummaryCards totals={totals} />

      {swapPending.length > 0 ? (
        <section className="mb-4 overflow-hidden rounded-xl border border-amber-500/30 bg-card shadow-sm">
          <div className="flex items-center justify-between border-amber-500/20 border-b bg-amber-500/10 px-4 py-3 sm:px-5">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground text-base">
              <ArrowLeftRight className="size-4 text-amber-700 dark:text-amber-400" />
              ຄຳຂໍແລກກະ
            </h2>
            <Badge variant="secondary" className="tabular-nums">
              {swapPending.length} ລໍຖ້າ
            </Badge>
          </div>
          <ul className="space-y-3 p-4 sm:p-5">
            {swapPending.map((row) => (
              <SwapPendingCard
                key={row.id}
                row={row}
                busy={swapBusy}
                onApprove={() => onApproveSwap(row.id)}
                onReject={() => onRejectSwap(row.id)}
              />
            ))}
          </ul>
        </section>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-border border-b bg-muted/25 px-4 py-3 sm:px-5">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground text-base">
              <CalendarDays className="size-4 text-violet-600 dark:text-violet-400" />
              ຕາມວັນ
            </h2>
            <span className="text-muted-foreground text-xs">
              <Clock className="mr-1 inline size-3.5" aria-hidden />
              ອາທິດນີ້
            </span>
          </div>
          {totals.assignments === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="font-medium text-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນໃນອາທິດນີ້
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                ກະຈະຂຶ້ນຫຼັງພະນັກງານເປີດກະຈາກ POS
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {weekRows.map((row) => (
                <DayReportRow
                  key={row.workDate}
                  row={row}
                  maxAssignments={maxAssignments}
                  highlighted={highlightDayKey === row.workDate}
                />
              ))}
            </ul>
          )}
        </section>

        <section className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-border border-b bg-muted/25 px-4 py-3 sm:px-5">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground text-base">
              <UserRound className="size-4 text-sky-600 dark:text-sky-400" />
              ຕາມພະນັກງານ
            </h2>
            <span className="font-medium text-muted-foreground text-xs tabular-nums">
              {byStaff.length} ຄົນ
            </span>
          </div>
          {byStaff.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="font-medium text-foreground text-sm">
                ບໍ່ມີພະນັກງານໃນຊ່ວງນີ້
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {byStaff.map((row) => (
                <StaffReportRow key={row.shopStaffId} row={row} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}